import { type RefObject, useEffect, useState } from 'react';

interface ElementRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

/**
 * Tracks an element's live viewport-relative position/size while `active`,
 * refreshed on scroll (any ancestor, via capture) and resize. Used to pin
 * portaled content — e.g. a dropdown escaping a dialog's overflow clipping —
 * to wherever its trigger visually sits.
 */
export function useElementRect(ref: RefObject<HTMLElement | null>, active: boolean): ElementRect | null {
    const [
        rect,
        setRect,
    ] = useState<ElementRect | null>(null);

    useEffect(() => {
        if (!active) {
            return undefined;
        }

        const update = () => {
            const r = ref.current?.getBoundingClientRect();

            if (r) {
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            }
        };

        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);

        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [
        active,
        ref,
    ]);

    return rect;
}
